import { useState, type FormEvent } from "react";
import { contactMessageSent } from "../../../shared/src/contact-messages";
import { Input } from "./ui-kit/forms";
import "../../src/ui/tokens.css";
import "../../src/ui/forms.css";
import "../../src/ui/contact-form.css";

/**
 * A creator's Contact form, in authored pages, Contact form sections and editor previews: a
 * visitor's name, email and message, kept for the creator under Members › Inquiries. It makes
 * no account and sends no email.
 */
export function ContactForm({
  id,
  buttonLabel = "Send message",
  preview = false,
  pagePath = "/",
}: {
  id: string;
  buttonLabel?: string;
  preview?: boolean;
  pagePath?: string;
}) {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "limited" | "failed">("idle");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (preview || status === "sending") return;
    const form = event.currentTarget;
    const fields = new FormData(form);
    setStatus("sending");
    try {
      const response = await fetch("/api/contact-messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fields.get("name"),
          email: fields.get("email"),
          message: fields.get("message"),
          website: fields.get("website"),
          pagePath: location.pathname,
        }),
      });
      if (response.status === 429) {
        setStatus("limited");
        return;
      }
      const result = (await response.json()) as { status?: string };
      if (!response.ok || result.status !== "sent") throw new Error("Message failed");
      form.reset();
      setStatus("sent");
    } catch {
      setStatus("failed");
    }
  }
  return (
    <form
      action="/api/contact-messages"
      method="post"
      className="ui-contact-form"
      data-ui="contact-form"
      onSubmit={(event) => void submit(event)}
    >
      <fieldset disabled={preview || status === "sending" || status === "sent"}>
        <div className="ui-contact-form-row">
          <Input
            id={`${id}-name`}
            name="name"
            label="Name"
            autoComplete="name"
            required
            maxLength={100}
          />
          <Input
            id={`${id}-email`}
            name="email"
            label="Email address"
            type="email"
            autoComplete="email"
            required
            maxLength={320}
            placeholder="you@example.com"
          />
        </div>
        <Input
          id={`${id}-message`}
          name="message"
          label="Message"
          multiline
          rows={6}
          required
          maxLength={5000}
        />
        <div hidden aria-hidden="true">
          <label>
            Website
            <input name="website" tabIndex={-1} autoComplete="off" />
          </label>
        </div>
        <input type="hidden" name="pagePath" value={pagePath} />
        <button className="button button-primary" type="submit">
          {status === "sending" ? "Sending…" : buttonLabel}
        </button>
      </fieldset>
      <p role="status" aria-live="polite">
        {status === "sent"
          ? contactMessageSent
          : status === "limited"
            ? "That’s a lot of messages at once. Please try again in a little while."
            : status === "failed"
              ? "We couldn’t send your message. Please try again."
              : preview
                ? "Preview: messages aren’t sent from here."
                : ""}
      </p>
    </form>
  );
}
